"use client";

import { useMemo, useState } from "react";

import Image from "next/image";
// CMS-supplied hrefs are emitted verbatim - do NOT swap this for @repo/ui/navigation.
// The CMS localizes its own URLs (e.g. "cl/es/centro-de-ayuda/") and also returns
// external/relative ones, so prefixing here would double or corrupt the locale.
import Link from "next/link";

import { addExtrasToBookingInState } from "@repo/ui/api/addExtrasToBookingInState";
import { getBookingInstate } from "@repo/ui/api/getBookingInstate";
import { removeSssrsFromBookingInState } from "@repo/ui/api/removeSssrsFromBookingInState";
import { useExtrasStatus } from "@repo/ui/components/Extras/common/useExtrasStatus";
import { DIRECTION_LABELS, DIRECTIONS } from "@repo/ui/constants/directions";
import { SectionScrollIds } from "@repo/ui/constants/scrollIds";
import { SSR_CODES, SSR_CODES_CHECKIN } from "@repo/ui/constants/ssrs";
import { USER_ROLES } from "@repo/ui/constants/userRoles";
import { getDirection } from "@repo/ui/helpers/Extras/getDirection";
import {
  getJourneySsrMaxSelections,
  isSsrInAnyJourneyInBundle,
} from "@repo/ui/helpers/Extras/journeySsrUtils";
import { calculatePetOnBoardPrice } from "@repo/ui/helpers/petPricing";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import type { CommonInfoSection, PetOnBoardSection } from "@repo/ui/types/cms";
import type { JourneyWithPassengerSegments } from "@repo/ui/types/extras";
import type { BookingSummary } from "@repo/ui/types/manageBooking";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import {
  clearPetOnBoarding,
  selectBookingDetails,
  selectIsCheckIn,
  selectSsrAvailability,
  setBookingDetails,
  setPetOnBoardButtonStatus,
  useInboundExtrasSelection,
  useIsPostBooking,
  useIsUserStaff,
  useOutboundExtrasSelection,
} from "@repo/store/slices";

import { ExtrasPetOnBoardOptionCard } from "./ExtrasPetOnBoardOptionCard";

interface ExtrasPetOnBoardSectionCardProps {
  journeys?: JourneyWithPassengerSegments[];
  cmsData?: PetOnBoardSection;
  commonInfo?: CommonInfoSection;
  retrieveBooking?: BookingSummary | null;
}

export default function ExtrasPetOnBoardSectionCard({
  journeys,
  cmsData,
  commonInfo,
  retrieveBooking,
}: Readonly<ExtrasPetOnBoardSectionCardProps>) {
  const dispatch = useAppDispatch();
  const outboundExtras = useOutboundExtrasSelection();
  const inboundExtras = useInboundExtrasSelection();
  const isCheckin = useAppSelector(selectIsCheckIn);
  const isPostBooking = useIsPostBooking();
  const isBookingFlow = !isCheckin && !isPostBooking;
  const bookingDetails = useAppSelector(selectBookingDetails);
  const checkingStatus = useExtrasStatus();
  const ssrAvailability = useAppSelector(selectSsrAvailability);

  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);

  const petOnBoardPolicyUrl = cmsData?.conditionsLinkCta ?? process.env.PET_ON_BOARD_POLICY_ES_URL;

  const petOnBoardCodes: string[] =
    isCheckin || isPostBooking
      ? [SSR_CODES_CHECKIN.PET_ON_BOARD, SSR_CODES.PET_ON_BOARD]
      : [SSR_CODES.PET_ON_BOARD];

  // Derived state to track which passenger/direction already has pet on board added
  const addedPets = useMemo(() => {
    if (!journeys || journeys.length === 0) return {};

    const computed: Record<string, boolean> = {};
    for (const [jIndex, journey] of journeys.entries()) {
      const direction = getDirection(jIndex);
      const isOutbound = direction === DIRECTIONS.OUTBOUND;
      const paxList = Object.values(journey.passengerSegment);
      for (const pax of paxList) {
        const passengerKey = pax.passengerKey;
        const petData = isOutbound
          ? outboundExtras?.petOnBoard?.[passengerKey]
          : inboundExtras?.petOnBoard?.[passengerKey];
        // Check if pet is added (handling both number and PetOnBoardItem types)
        const isPetAdded =
          petData !== undefined && (typeof petData === "number" ? petData > 0 : true);
        computed[`${passengerKey}_${direction}`] = isPetAdded;
      }
    }
    return computed;
  }, [journeys, outboundExtras?.petOnBoard, inboundExtras?.petOnBoard]);

  const isPetOnBoardInAnyBundle = isSsrInAnyJourneyInBundle(journeys, SSR_CODES.PET_ON_BOARD);
  const isUserStaff = useIsUserStaff();

  // Track first passenger keys for each direction
  const firstPassengerKeys = useMemo(() => {
    const keys: Record<string, string> = {};
    if (journeys) {
      for (const [jIndex, journey] of journeys.entries()) {
        const direction = getDirection(jIndex);
        const firstPassenger = Object.values(journey.passengerSegment)[0];
        if (firstPassenger) {
          keys[direction] = firstPassenger.passengerKey;
        }
      }
    }
    return keys;
  }, [journeys]);

  const userType = isUserStaff ? USER_ROLES.Staff : USER_ROLES.Anonymous;

  const handleToggle = async (
    passengerKey: string,
    price: number,
    direction: string,
    ssrKey?: string,
  ) => {
    const isOutbound = direction === DIRECTIONS.OUTBOUND;
    const map = isOutbound ? outboundExtras?.petOnBoard : inboundExtras?.petOnBoard;

    // Check if pet is already added (handling both number and PetOnBoardItem types)
    const petData = map?.[passengerKey];
    const isPetAdded = petData !== undefined && (typeof petData === "number" ? petData > 0 : true);
    const isAdding = !isPetAdded;

    const baseKey = `${passengerKey}_${direction}`;

    const loadingKey = `${baseKey}_${isAdding ? "add" : "remove"}`;

    try {
      setLoadingKeys((prev) => [...prev, loadingKey]);
      const tabSessionId = getTabSessionId();
      const currencyCode = bookingDetails?.currencyCode ?? "CLP";

      const isSSRAvailable = !!ssrKey;
      const finalPrice = calculatePetOnBoardPrice(
        userType,
        passengerKey,
        firstPassengerKeys,
        direction,
        price,
        isSSRAvailable,
      );

      if (isAdding) {
        if (ssrKey) {
          const updatedPayload = {
            baggages: [
              {
                name: "Pet on Board",
                quantity: 1,
                ssrCode: bookingDetails?.recordLocator
                  ? SSR_CODES_CHECKIN.PET_ON_BOARD
                  : SSR_CODES.PET_ON_BOARD,
                ssrKey: ssrKey,
                passengerKey: passengerKey,
                isStaffEntitled: isUserStaff,
                staffEntitledAmount: finalPrice,
                journeyKey: isOutbound
                  ? bookingDetails?.journeys?.[0]?.journeyKey
                  : bookingDetails?.journeys?.[1]?.journeyKey,
              },
            ],
            currency: currencyCode,
            isUserStaff,
          };

          await addExtrasToBookingInState(updatedPayload, tabSessionId);
        }
      } else {
        if (ssrKey) {
          await removeSssrsFromBookingInState({ ssrKeys: [ssrKey] }, tabSessionId);
        }
      }

      const booking = await getBookingInstate(tabSessionId);
      if (booking) {
        dispatch(setBookingDetails(booking));
      }
      if (isAdding) {
        dispatch(
          setPetOnBoardButtonStatus({
            isAdded: true,
            price: finalPrice,
            direction,
            passengerId: passengerKey,
            cartType: isCheckin ? "checkInCart" : "bookingCart",
            ssrKey: ssrKey || "",
            ssrCode: isBookingFlow ? SSR_CODES.PET_ON_BOARD : SSR_CODES_CHECKIN.PET_ON_BOARD,
          }),
        );
      } else {
        dispatch(
          clearPetOnBoarding({
            direction,
            passengerKey,
            cartType: isCheckin ? "checkInCart" : "bookingCart",
          }),
        );
      }
    } finally {
      setLoadingKeys((prev) => prev.filter((k) => k !== loadingKey));
    }
  };

  return (
    <div
      className="flex flex-col-reverse my-3 md:flex-row"
      id={SectionScrollIds.PetOnboard}
      data-testid="extras-pet-on-board-section"
    >
      <div className="flex flex-col w-full p-5 space-y-4 bg-white border rounded-xl">
        <div className="flex items-start gap-4">
          {cmsData?.heading?.icon && (
            <Image
              src={cmsData?.heading?.icon}
              width={50}
              height={50}
              alt="logo"
              data-testid="extras-pet-on-board-logo-image"
            />
          )}
          <div className="flex flex-col space-y-1">
            {cmsData?.heading?.headline && (
              <h3
                className="text-xl font-bold text-brand-blue"
                data-testid="extras-pet-on-board-headline-lbl"
              >
                {cmsData?.heading?.headline}
              </h3>
            )}
            {cmsData?.heading?.ctaText && (
              <span
                className="text-sm font-normal text-brand-blue"
                data-testid="extras-pet-on-board-cta-lbl"
              >
                {cmsData?.heading?.ctaText}
              </span>
            )}
          </div>
        </div>

        <div
          className={
            journeys?.length === 1
              ? "flex justify-center w-full"
              : "grid grid-cols-1 gap-4 xl:grid-cols-2"
          }
        >
          {journeys?.map((journey, jIndex) => {
            const direction = getDirection(jIndex);
            const directionLabel =
              direction === DIRECTIONS.OUTBOUND
                ? commonInfo?.outboundLabel || DIRECTION_LABELS.OUTBOUND
                : commonInfo?.returnLabel || DIRECTION_LABELS.INBOUND;

            const isJourneyDisabled =
              direction === DIRECTIONS.OUTBOUND
                ? checkingStatus?.isOutboundClose
                : checkingStatus?.isInboundClose;
            const defaultMaxSelections = getJourneySsrMaxSelections(journey, petOnBoardCodes);
            const petAvailability = bookingDetails?.journeys
              ?.find((j) => j.journeyKey === journey.journeyKey)
              ?.segments?.flatMap((s) => s.legs)
              ?.flatMap((l) => l.ssrs || [])
              ?.find((ssr) => ssr.ssrNestCode === "PTC");

            let remainingPets = petAvailability?.available;

            if (remainingPets === undefined) {
              const ssrAvailabilityForJourney = ssrAvailability?.journeySsrs?.find(
                (j) => j.journeyKey === journey.journeyKey,
              );
              const petSsrFromAvail = ssrAvailabilityForJourney?.ssrs?.find(
                (ssr) => ssr.ssrCode != null && petOnBoardCodes.includes(ssr.ssrCode),
              );
              remainingPets = petSsrFromAvail?.available ?? 0;
            }
            const maxSelections = Math.min(4, defaultMaxSelections, remainingPets ?? 4);

            return (
              <div
                key={journey.journeyKey}
                className={journeys?.length === 1 ? "w-full md:w-1/2 max-w-[600px]" : ""}
                data-testid={`extra-pet-on-board-journey-${journey.journeyKey}-section`}
              >
                <ExtrasPetOnBoardOptionCard
                  InBundleInAnyJourney={isPetOnBoardInAnyBundle}
                  direction={direction}
                  directionLabel={directionLabel}
                  origin={journey.designator.origin}
                  destination={journey.designator.destination}
                  options={journey.passengerSegment}
                  addedPets={addedPets}
                  bundleCode={journey?.bundleCode}
                  maxSelections={maxSelections}
                  remainingPets={remainingPets}
                  onChangeAction={(
                    passengerKey: string,
                    price: string | number,
                    ssrKey?: string,
                  ) => {
                    const option = Object.values(journey.passengerSegment).find(
                      (opt) => opt.passengerKey === passengerKey,
                    );
                    if (option) {
                      handleToggle(passengerKey, Number(price), direction, ssrKey);
                    }
                  }}
                  commonInfo={commonInfo}
                  cmsData={cmsData}
                  firstPassengerKeys={firstPassengerKeys}
                  userType={userType}
                  passengers={retrieveBooking?.passengers}
                  isJourneyDisabled={isJourneyDisabled}
                  loadingKeys={loadingKeys}
                />
              </div>
            );
          })}
        </div>

        {petOnBoardPolicyUrl && cmsData?.conditionsLinkText && (
          <Link
            href={petOnBoardPolicyUrl}
            target="_blank"
            className="underline hover:text-[#0F1E33]"
            data-testid="pet-on-board-policy-url-link"
          >
            {cmsData?.conditionsLinkText}
          </Link>
        )}
      </div>
    </div>
  );
}
